/* eslint-disable no-param-reassign */
import { navigateTo } from '#imports'
import { type Ref, type ShallowReactive, onMounted, watch } from 'vue'
import {
  type LocationQueryRaw,
  type RouteLocationNormalizedLoaded,
  useRoute,
} from 'vue-router'

import { parse } from '@backmarket/utils/query-string/parse'
import { stringify } from '@backmarket/utils/query-string/stringify'

export function useFiltersFromHash(
  filtersInput: ShallowReactive<Record<string, string[]>>,
  priceInput: Ref<[number, number]>,
  pageInput: Ref<number>,
  {
    route = useRoute(),
  }: {
    route?: Partial<RouteLocationNormalizedLoaded>
  } = {},
) {
  const initMaxPrice = priceInput.value[1]
  function getFiltersFromUrl() {
    const { price, ...parsedQuery } = route.hash
      ? parse(route.hash)
      : { price: [] }

    const p = route.query
      ? parseInt(route.query.p?.toString() || '', 10) || 1
      : 1

    if (Array.isArray(price) && price.length === 2 && price[0] && price[1]) {
      priceInput.value = [parseInt(price[0], 10), parseInt(price[1], 10)]
    }
    for (const [key, val] of Object.entries(parsedQuery)) {
      if (Array.isArray(val)) {
        filtersInput[key] = val.filter((item) => item !== null) as string[]
      } else if (val !== null) {
        filtersInput[key] = [val]
      }
    }

    if (p > 1) {
      pageInput.value = p
    }
  }

  function cleanQueryParams(queryParams: LocationQueryRaw) {
    if (queryParams.p === 1) {
      const { p, ...rest } = queryParams

      return rest
    }

    return queryParams
  }

  async function pushHashFiltersToRouter() {
    pageInput.value = 1
    const filters =
      priceInput.value[0] !== 0 || priceInput.value[1] !== initMaxPrice
        ? { ...filtersInput, price: priceInput.value }
        : filtersInput
    const hashForActiveFilters = stringify(filters)

    if (route.name) {
      await navigateTo({
        name: route.name,
        query: cleanQueryParams({ ...route.query }),
        params: route.params,
        hash: `#${hashForActiveFilters}`,
      })
    }
  }

  async function pushQueryFiltersToRouter() {
    if (route.name) {
      await navigateTo({
        name: route.name,
        query: cleanQueryParams({ ...route.query, p: pageInput.value }),
        params: route.params,
        hash: route.hash,
      })
      if (import.meta.client) window.scrollTo(0, 0)
    }
  }

  onMounted(() => {
    getFiltersFromUrl()
  })

  watch(
    [filtersInput, priceInput],
    async () => {
      await pushHashFiltersToRouter()
    },
    { deep: true },
  )
  watch(pageInput, async () => {
    await pushQueryFiltersToRouter()
  })
}
